import {DayTypeDTO, PeriodDTO} from '../bell-schedule/models/bell-schedule.model';
import {Term} from '../shared/models/term.model';
import {ScheduleSection} from './schedule-section.model';
import {CourseLink} from './course-link.model';
import {SectionLink} from './section-link.model';
import {SchedulingSettingsDTO} from '../shared/scheduling-settings/scheduling.settings.model';
import {ScheduleTeacher} from '../schedule-teacher.model';

export class ScheduleSlot {

    // Static Properties
    public static Displays = {
        Period: 'Period Label',
        Day: 'Day',
        Term: 'Term'
    };

    // Slot Properties
    SlotName: string;

    // Reference Properties
    periods: PeriodDTO[] = [];
    dayType: DayTypeDTO;
    term: Term;
    scheduledSection: ScheduleSection;
    greyedOut: boolean;
    scrollTo = false;
    changed = false;
    topActionMenuItems: string[] = [];
    bottomActionMenuItems: string[] = [];
    courseCount: number;

    // Constructor
    constructor(periods: PeriodDTO[], dayType: DayTypeDTO, term: Term, order: string[]) {

        // Setting internal properties
        this.periods = periods;
        if (!this.periods) {
            this.periods = [];
        }
        this.dayType = dayType;
        this.term = term;
        this.greyedOut = false;

        // Setting the slot name
        const periodName = this.periods.length > 0 ? this.periods[0].PeriodName : '';
        const dayName = this.dayType ? this.dayType.DayName : (this.periods.length > 0 ? this.periods[0].DayTypeName : '');
        const termName = this.term ? this.term.ShortDescription : '';

        // Setting the slot name
        this.SlotName = '';
        const appendName = (name: string) => {
            if (name) {
                if (this.SlotName) {
                    this.SlotName += ' - ';
                }
                this.SlotName += name;
            }
        };
        order.forEach(type => {
            switch (type) {
                case ScheduleSlot.Displays.Period:
                    appendName(periodName);
                    break;

                case ScheduleSlot.Displays.Day:
                    appendName(dayName);
                    break;

                case ScheduleSlot.Displays.Term:
                    appendName(termName);
                    break;
            }
        });
    }

    get ClassName(): string {
        return this.scheduledSection ? this.scheduledSection.ClassName : null;
    }

    get StudentsInSlot(): number {
        return this.scheduledSection ? this.scheduledSection.StudentsAssignedToSection : null;
    }

    get SeatsInSlot(): number {
        return this.scheduledSection ? this.scheduledSection.MaxSeatsOfSection : null;
    }

    get TeacherNames(): string {
        return this.scheduledSection ? this.scheduledSection.teacherNames : null;
    }

    get RoomName(): string {
        return this.scheduledSection ? this.scheduledSection.ClassRoomName : null;
    }

    get MeetingPatternName(): string {
        return this.scheduledSection ? this.scheduledSection.MeetingPatternName : null;
    }

    get TermName(): string {
        return this.scheduledSection ? this.scheduledSection.TermName : null;
    }

    get Start(): Date {
        return this.scheduledSection ? this.scheduledSection.StartDate : null;
    }

    set Start(date: Date) {
        if (this.scheduledSection) {
            this.scheduledSection.StartDate = date;
        }
    }

    get End(): Date {
        return this.scheduledSection ? this.scheduledSection.EndDate : null;
    }

    set End(date: Date) {
        if (this.scheduledSection) {
            this.scheduledSection.EndDate = date;
        }
    }

    get SubjectArea(): string {
        return this.scheduledSection ? this.scheduledSection.SubjectArea : null;
    }

    get Department(): string {
        return this.scheduledSection ? this.scheduledSection.Department : null;
    }

    get CreditCode(): string {
        return this.scheduledSection ? this.scheduledSection.CreditCode : null;
    }

    get SectionLinks(): SectionLink[] {
        return this.scheduledSection ? this.scheduledSection.SectionLinks : [];
    }

    get CourseLinks(): CourseLink[] {
        return this.scheduledSection ? this.scheduledSection.CourseLinks : [];
    }

    get CourseSection(): string {
        return this.scheduledSection ? this.scheduledSection.courseSection : '';
    }

    get CourseLinkConcatenated(): string {
        let concatenatedString = '';
        for (let i = 0; i < this.CourseLinks.length; i++) {
            if (concatenatedString.length > 0) {
                concatenatedString += ',';
            }
            concatenatedString += this.CourseLinks[i].Name;
        }
        return concatenatedString;
    }

    get SectionLinkConcatenated(): string {
        let concatenatedString = '';
        for (let i = 0; i < this.SectionLinks.length; i++) {
            if (concatenatedString.length > 0) {
                concatenatedString += ',';
            }
            concatenatedString += this.SectionLinks[i].Name;
        }
        return concatenatedString;
    }

    get teacherDisplayString(): string {
        return this.scheduledSection ? this.scheduledSection.teacherDisplayString : '';
    }

    get WithdrawalCode(): string {
        return this.scheduledSection ? this.scheduledSection.WithdrawalCode : '';
    }

    set WithdrawalCode(code: string) {
        if (this.scheduledSection) {
            this.scheduledSection.WithdrawalCode = code;
        }
    }

    // Methods
    setScheduleSection(section: ScheduleSection, schedulingSettings: SchedulingSettingsDTO) {
        // PRE: a section to set to the slot
        // POST: sets the internal data from the section to the slot
        this.scheduledSection = section;
        this.scheduledSection.getCourseSection(schedulingSettings);
        if (section != null && section.WithdrawalCode != null) {
            this.greyedOut = true;
        }
    }

    getTeachersDisplayString(teachers: ScheduleTeacher[]) {
        // POST: obtains the teachers that should display for the scheduleClass
        if (this.scheduledSection) {
            this.scheduledSection.getTeachersDisplayString(teachers);
        }
        return this.teacherDisplayString;
    }
}
